import { useRef, useState, useEffect, useMemo } from 'react';

import { usePostOfficeExernalMessageProxy } from '@atlassian/post-office-context';

import { fg } from '@confluence/feature-gating';

import {
	useIsPulseActive,
	useDiscoverabilityPulseActions as useBaseDiscoverabilityPulseActions,
	havePulsesEnded,
	endPulses,
} from './useDiscoverabilityPulseStatePostOffice';

const PULSE_MESSAGE_IDS = [
	'cc-loom-x-sell-discoverability-pulse-1',
	'cc-loom-x-sell-discoverability-pulse-2',
	'cc-loom-x-sell-discoverability-pulse-3',
	'cc-loom-x-sell-discoverability-pulse-4',
	'cc-loom-x-sell-discoverability-pulse-5',
] as const;

const TIMEOUT_MS = 300;
const LAST_PULSE_INDEX = PULSE_MESSAGE_IDS.length - 1;

/**
 * Helper function to request the next pulse message
 */
type PostOfficePulseHook = ReturnType<typeof usePostOfficeExernalMessageProxy> & {
	handlePostOfficeClickTracking?: () => void;
};

const requestNextPulse = (
	nextIndex: number,
	pulseHooks: PostOfficePulseHook[],
	hasRequestedRef: React.MutableRefObject<Set<number>>,
	lastRequestedRef: React.MutableRefObject<{ index: number; timestamp: number } | null>,
) => {
	if (nextIndex < pulseHooks.length && !hasRequestedRef.current.has(nextIndex)) {
		hasRequestedRef.current.add(nextIndex);
		lastRequestedRef.current = { index: nextIndex, timestamp: Date.now() };
		pulseHooks[nextIndex].requestMessageStart();
	}
};

export const useDiscoverabilityPulsePostOffice = (entryPointId: string) => {
	// All hooks must be called unconditionally (Rules of Hooks)
	const isPulseActive = useIsPulseActive(entryPointId);
	const { start: startPulse } = useBaseDiscoverabilityPulseActions();
	const skipRefreshPulseStatus = isPulseActive && havePulsesEnded();

	// Call hooks at the top level for all message IDs
	const pulse1 = usePostOfficeExernalMessageProxy(PULSE_MESSAGE_IDS[0]) as PostOfficePulseHook;
	const pulse2 = usePostOfficeExernalMessageProxy(PULSE_MESSAGE_IDS[1]) as PostOfficePulseHook;
	const pulse3 = usePostOfficeExernalMessageProxy(PULSE_MESSAGE_IDS[2]) as PostOfficePulseHook;
	const pulse4 = usePostOfficeExernalMessageProxy(PULSE_MESSAGE_IDS[3]) as PostOfficePulseHook;
	const pulse5 = usePostOfficeExernalMessageProxy(PULSE_MESSAGE_IDS[4]) as PostOfficePulseHook;

	const pulseHooks = useMemo(
		() => [pulse1, pulse2, pulse3, pulse4, pulse5],
		[pulse1, pulse2, pulse3, pulse4, pulse5],
	);

	// Track which messages we've requested to avoid duplicate requests
	const hasRequestedRef = useRef<Set<number>>(new Set());
	// Track the last requested message index and when it was requested (for timeout)
	const lastRequestedRef = useRef<{ index: number; timestamp: number } | null>(null);
	// Track which message index was last started (to detect dismissal)
	const lastStartedIndexRef = useRef<number | null>(null);
	// State trigger to force re-evaluation when timeout expires
	const [timeoutTrigger, setTimeoutTrigger] = useState(0);

	// Request messages sequentially - only request the next one if previous ones haven't started
	useEffect(() => {
		if (!fg('confluence_loom_x-sell_ep_po_migration') || skipRefreshPulseStatus) {
			return;
		}

		// Find the first message that hasn't started and request it
		// If a previous message has started, we stop (handled by the other effect)
		for (let i = 0; i < pulseHooks.length; i++) {
			const pulseHook = pulseHooks[i];

			// If this message has started, we're done - don't request any more
			if (pulseHook.hasMessageStarted) {
				// Clear the timeout tracking since we found an active message
				lastRequestedRef.current = null;
				// Track which message is started (will be set by the activation effect, but set here too for consistency)
				lastStartedIndexRef.current = i;
				break;
			}

			// If we haven't requested this one yet, request it and stop
			// The effect will re-run when hasMessageStarted changes, allowing us to move to the next one
			if (!hasRequestedRef.current.has(i)) {
				hasRequestedRef.current.add(i);
				lastRequestedRef.current = { index: i, timestamp: Date.now() };
				pulseHook.requestMessageStart();
				break;
			}
		}
	}, [
		skipRefreshPulseStatus,
		pulseHooks,
		pulse1.hasMessageStarted,
		pulse2.hasMessageStarted,
		pulse3.hasMessageStarted,
		pulse4.hasMessageStarted,
		pulse5.hasMessageStarted,
		pulse1.requestMessageStart,
		pulse2.requestMessageStart,
		pulse3.requestMessageStart,
		pulse4.requestMessageStart,
		pulse5.requestMessageStart,
		pulse1.handlePostOfficeClickTracking,
		pulse2.handlePostOfficeClickTracking,
		pulse3.handlePostOfficeClickTracking,
		pulse4.handlePostOfficeClickTracking,
		pulse5.handlePostOfficeClickTracking,
	]);

	// Timeout handler: if a requested message hasn't started after a delay, try the next one
	// Note: If a message was already dismissed, Post Office filters it before page load
	// so we only need time for the initial load and state updates (~200-300ms)
	useEffect(() => {
		if (
			!fg('confluence_loom_x-sell_ep_po_migration') ||
			skipRefreshPulseStatus ||
			!lastRequestedRef.current ||
			isPulseActive
		) {
			return;
		}

		const { index, timestamp } = lastRequestedRef.current;
		const pulseHook = pulseHooks[index];

		// If the message has started, clear the timeout tracking
		if (pulseHook.hasMessageStarted) {
			lastRequestedRef.current = null;
			return;
		}

		// If enough time has passed and the message still hasn't started, move to the next one
		const elapsed = Date.now() - timestamp;

		if (elapsed >= TIMEOUT_MS) {
			// Clear the current request tracking
			lastRequestedRef.current = null;

			// Try the next message if available
			requestNextPulse(index + 1, pulseHooks, hasRequestedRef, lastRequestedRef);
		} else {
			// Set a timeout to trigger re-evaluation when the timeout period elapses
			const remainingTime = TIMEOUT_MS - elapsed;
			const timeoutId = setTimeout(() => {
				setTimeoutTrigger((prev) => prev + 1);
			}, remainingTime);

			return () => clearTimeout(timeoutId);
		}
	}, [
		skipRefreshPulseStatus,
		isPulseActive,
		timeoutTrigger,
		pulseHooks,
		pulse1.hasMessageStarted,
		pulse2.hasMessageStarted,
		pulse3.hasMessageStarted,
		pulse4.hasMessageStarted,
		pulse5.hasMessageStarted,
	]);

	// Reactively respond when a message starts - activate the pulse
	useEffect(() => {
		if (!fg('confluence_loom_x-sell_ep_po_migration') || skipRefreshPulseStatus) {
			return;
		}

		// Check each pulse in sequence - activate the first one that has started
		for (let i = 0; i < pulseHooks.length; i++) {
			const pulseHook = pulseHooks[i];
			const messageId = PULSE_MESSAGE_IDS[i];

			if (pulseHook.hasMessageStarted) {
				lastStartedIndexRef.current = i;
				void startPulse(entryPointId, messageId, {
					onDismiss: () => pulseHook.dismissMessage(),
					onClick: () => pulseHook.handlePostOfficeClickTracking?.(),
				});

				// If this is the last pulse, mark pulses as ended
				if (i === LAST_PULSE_INDEX) {
					/**
					 * Mark the EP message as "complete" and disable Loom discoverability in localStorage
					 * immediately after showing the final pulse. Otherwise, if a user ignores the entrypoints
					 * entirely and never clicks on one while it's pulsing, they'll:
					 *
					 * - Remain in the TAP audience forever
					 * - Continue to check the EP messages every time an entrypoint renders
					 *
					 * This will technically unlock EP to show other messages, but the pulse is subtle enough that
					 * this shouldn't result in poor UX
					 */
					endPulses();
				}

				// Stop checking once we've found an active pulse
				break;
			}
		}
	}, [
		skipRefreshPulseStatus,
		entryPointId,
		startPulse,
		pulseHooks,
		pulse1.hasMessageStarted,
		pulse2.hasMessageStarted,
		pulse3.hasMessageStarted,
		pulse4.hasMessageStarted,
		pulse5.hasMessageStarted,
	]);

	// Handle message dismissal: if a started message becomes not started, request the next one
	useEffect(() => {
		if (
			!fg('confluence_loom_x-sell_ep_po_migration') ||
			skipRefreshPulseStatus ||
			lastStartedIndexRef.current === null ||
			isPulseActive
		) {
			return;
		}

		const startedIndex = lastStartedIndexRef.current;
		const startedPulseHook = pulseHooks[startedIndex];

		// If the message that was started is no longer started, it was dismissed
		if (!startedPulseHook.hasMessageStarted) {
			// Clear the tracking for this message so we can move forward
			hasRequestedRef.current.delete(startedIndex);
			lastStartedIndexRef.current = null;

			// Request the next message if available
			requestNextPulse(startedIndex + 1, pulseHooks, hasRequestedRef, lastRequestedRef);
		}
	}, [
		skipRefreshPulseStatus,
		isPulseActive,
		pulseHooks,
		pulse1.hasMessageStarted,
		pulse2.hasMessageStarted,
		pulse3.hasMessageStarted,
		pulse4.hasMessageStarted,
		pulse5.hasMessageStarted,
	]);

	return fg('confluence_loom_x-sell_ep_po_migration')
		? { isPulseActive }
		: { isPulseActive: false };
};
